import React from "react";
import { BookOpen, Scale } from "lucide-react";
import type { ContentSection } from "@/content/types";

// Cover art for a blog or comparison page, drawn rather than uploaded so a
// new page needs no image: the page's label set big on the brand ink, with
// two soft glows whose placement comes from the slug (stable per page,
// different between pages) and a dot grid. A comparison labelled "vs X" is
// drawn as a matchup. Decorative, so hidden from screen readers.

const hash = (s: string) =>
  [...s].reduce((h, c) => (h * 31 + c.charCodeAt(0)) >>> 0, 7);

const GLOWS = [
  ["tw--left-[20%] tw--top-[45%]", "tw--bottom-[55%] tw--right-[15%]"],
  ["tw--right-[25%] tw--top-[50%]", "tw--bottom-[60%] tw--left-[10%]"],
  ["tw--left-[30%] tw--bottom-[60%]", "tw--right-[20%] tw--top-[55%]"],
];

const THEMES = {
  blog: { icon: BookOpen, glow: "tw-bg-[#C08BFF] tw-opacity-40" },
  compare: { icon: Scale, glow: "tw-bg-[#E07BFF] tw-opacity-35" },
};

interface Props {
  section: ContentSection;
  slug: string;
  kind: string;
  // Big words on the cover, e.g. "RVC explained" or "vs Kits.ai".
  label?: string;
  size?: "hero" | "card";
  className?: string;
}

export default function CoverArt({
  section,
  slug,
  kind,
  label,
  size = "card",
  className = "",
}: Props) {
  const theme = THEMES[section];
  const Icon = theme.icon;
  const hero = size === "hero";
  const [purple, lime] = GLOWS[hash(slug) % GLOWS.length];
  const rival = label?.match(/^vs\.? (.+)$/i)?.[1];
  const chip = `jam-display tw-inline-flex tw-items-center tw-rounded-full tw-font-extrabold tw-leading-none ${
    hero
      ? "tw-px-4 tw-py-2.5 tw-text-[22px] md:tw-px-6 md:tw-py-4 md:tw-text-[34px]"
      : "tw-px-3 tw-py-2 tw-text-[17px]"
  }`;
  return (
    <div
      aria-hidden="true"
      className={`tw-relative tw-isolate tw-flex tw-flex-col tw-justify-between tw-overflow-hidden tw-bg-jam-ink ${
        hero
          ? "tw-min-h-[190px] tw-rounded-[26px] tw-p-5 md:tw-min-h-[340px] md:tw-rounded-[30px] md:tw-p-8"
          : "tw-h-[150px] tw-rounded-[22px] tw-p-4"
      } ${className}`}
    >
      <span
        className={`tw-absolute tw--z-10 tw-aspect-square tw-w-[85%] tw-rounded-full tw-bg-jam-purple tw-opacity-80 tw-blur-3xl ${purple}`}
      />
      <span
        className={`tw-absolute tw--z-10 tw-aspect-square tw-w-[70%] tw-rounded-full tw-blur-3xl ${theme.glow} ${lime}`}
      />
      <span
        className="tw-absolute tw-inset-0 tw--z-10 tw-opacity-[0.16]"
        style={{
          backgroundImage:
            "radial-gradient(circle, #fff 1px, transparent 1.4px)",
          backgroundSize: hero ? "22px 22px" : "16px 16px",
          maskImage: "linear-gradient(135deg, #000 10%, transparent 75%)",
          WebkitMaskImage: "linear-gradient(135deg, #000 10%, transparent 75%)",
        }}
      />
      <span className="tw-absolute tw-bottom-[6%] tw-right-[10%] tw--z-10">
        {(hero ? [520, 380, 240] : [260, 190, 120]).map((d) => (
          <span
            key={d}
            className="tw-absolute tw-rounded-full tw-border-solid tw-border-white/10"
            style={{
              width: d,
              height: d,
              left: -d / 2,
              top: -d / 2,
              borderWidth: hero ? 2 : 1.5,
            }}
          />
        ))}
      </span>
      <div className="tw-flex tw-items-center tw-justify-between">
        <span
          className={`tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-full tw-bg-white/90 tw-font-extrabold tw-text-jam-ink ${
            hero
              ? "tw-px-3.5 tw-py-1.5 tw-text-sm"
              : "tw-px-2.5 tw-py-1 tw-text-xs"
          }`}
        >
          <Icon size={hero ? 16 : 13} strokeWidth={2.6} />
          {kind}
        </span>
        <span
          className={`jam-display tw-font-extrabold tw-text-white/80 ${
            hero ? "tw-text-lg" : "tw-text-sm"
          }`}
        >
          Jammable
        </span>
      </div>
      {rival ? (
        <div
          className={`tw-flex tw-flex-wrap tw-items-center ${hero ? "tw-gap-3 md:tw-gap-4" : "tw-gap-2"}`}
        >
          <span className={`${chip} tw-bg-jam-lime tw-text-jam-ink`}>
            Jammable
          </span>
          <span
            className={`jam-display tw-inline-flex tw-items-center tw-justify-center tw-rounded-full tw-border-2 tw-border-solid tw-border-white/40 tw-font-extrabold tw-text-white ${
              hero
                ? "tw-h-11 tw-w-11 tw-text-base md:tw-h-14 md:tw-w-14 md:tw-text-xl"
                : "tw-h-8 tw-w-8 tw-text-xs"
            }`}
          >
            VS
          </span>
          <span className={`${chip} tw-bg-white tw-text-jam-ink`}>{rival}</span>
        </div>
      ) : (
        label && (
          <span
            className={`jam-display tw-max-w-[90%] tw-border-0 tw-border-l-[6px] tw-border-solid tw-border-jam-lime ${hero ? "tw-pl-4 md:tw-pl-5" : "tw-pl-3"} tw-font-extrabold tw-leading-[0.95] tw-tracking-[-0.02em] tw-text-white ${
              hero ? "tw-text-[40px] md:tw-text-[64px]" : "tw-text-[26px]"
            }`}
          >
            {label}
          </span>
        )
      )}
    </div>
  );
}
